import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchAllContacts,
  deleteContact,
  updateContact,
  addContact,
  addNote,
  updateNote,
  removeNote,
  setContactNotes,
} from '@store/actions'
import { IContact } from '@dataTypes/contact.type'
import { INote } from '@dataTypes/note.type'
import { FilterSelection } from '@components/common/SearchWithFilter/SearchWithFilter.component'

type ContactsState = {
  data: IContact[]
  searchQuery: string
  filters: FilterSelection
  loading: boolean
  error: string | null
  successMessage: string | null
  phoneNumber: string | null
}

const initialState: ContactsState = {
  data: [],
  searchQuery: '',
  filters: {
    city: [],
    state: [],
  },
  loading: false,
  error: null,
  successMessage: null,
  phoneNumber: null,
}

const contactsSlice = createSlice({
  name: 'contact',
  initialState,
  reducers: {
    setContactSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setContactFilters: (state, action: PayloadAction<FilterSelection>) => {
      state.filters = action.payload
    },
    setContactPhoneNumber: (state, action: PayloadAction<string | null>) => {
      state.phoneNumber = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContacts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllContacts.fulfilled,
        (state, action: PayloadAction<IContact[]>) => {
          state.data = action.payload
          state.loading = false
          state.successMessage = 'Contacts loaded successfully'
        },
      )
      .addCase(fetchAllContacts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    builder
      .addCase(addContact.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addContact.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(addContact.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    builder
      .addCase(updateContact.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateContact.fulfilled,
        (state, action: PayloadAction<IContact>) => {
          const index = state.data.findIndex(
            (contact) => contact.contact_id === action.payload.contact_id,
          )
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
            }
          }
          state.loading = false
          state.successMessage = 'Contacts updated successfully'
        },
      )
      .addCase(updateContact.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to update contact') as string
      })

    builder
      .addCase(deleteContact.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteContact.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.data = state.data.filter(
            (contact) => contact.contact_id !== action.payload,
          )
          state.successMessage = 'Contact removed successfully'
          state.error = null
        },
      )
      .addCase(deleteContact.rejected, (state) => {
        state.loading = false
        state.error = 'Failed to delete contact'
      })

    builder
      .addCase(addNote.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addNote.fulfilled, (state, action: PayloadAction<INote>) => {
        state.loading = false
        if (!action.payload.contact_id) return
        const note = action.payload
        const contactId = note.contact_id
        state.data = state.data.map((contact) =>
          contact.contact_id === contactId
            ? {
                ...contact,
                contact_note: [...(contact.contact_note || []), note],
              }
            : contact,
        )
        state.successMessage = 'Contact note added successfully'
      })
      .addCase(addNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to add contact note'
      })

    builder
      .addCase(updateNote.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateNote.fulfilled, (state, action: PayloadAction<INote>) => {
        state.loading = false
        if (!action.payload.contact_id) return
        const noteId = action.payload.note_id
        const updates: Partial<INote> = {
          title: action.payload.title,
          body: action.payload.body,
          updated_by: action.payload.updated_by ?? action.payload.created_by,
          ...(action.payload.date_time_updated_at && {
            date_time_updated_at: action.payload.date_time_updated_at,
          }),
        }
        state.data = state.data.map((contact) => ({
          ...contact,
          contact_note: contact.contact_note?.map((note) =>
            note.note_id === noteId ? { ...note, ...updates } : note,
          ),
        }))
        state.successMessage = 'Contact note updated successfully'
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to update contact note'
      })

    builder
      .addCase(removeNote.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeNote.fulfilled, (state, action: PayloadAction<string>) => {
        state.data = state.data.map((contact) => ({
          ...contact,
          contact_note: contact.contact_note?.filter(
            (note) => note.note_id !== action.payload,
          ),
        }))
        state.loading = false
        state.successMessage = 'Contact note deleted successfully'
      })
      .addCase(removeNote.rejected, (state, action) => {
        state.loading = false
        state.error =
          (action.payload as string) ??
          action.error?.message ??
          'Failed to delete contact note'
      })

    builder.addCase(
      setContactNotes,
      (state, action: PayloadAction<{ contactId: string; notes: INote[] }>) => {
        const { contactId, notes } = action.payload
        const index = state.data.findIndex((c) => c.contact_id === contactId)
        if (index !== -1) {
          state.data[index].contact_note = notes
        }
      },
    )
  },
})

export const {
  setContactSearchQuery,
  setContactFilters,
  setContactPhoneNumber,
} = contactsSlice.actions

export default contactsSlice.reducer
